import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaHome,
  FaUsers,
  FaCalendarAlt,
  FaCog,
  FaChevronDown,
  FaChevronRight,
  FaSignOutAlt,
  FaTimes,
  FaChartBar,
  FaBars,
  FaBell,
  FaBoxes,
  FaFlask,
  FaQuestionCircle,
} from 'react-icons/fa';
import { useAuthSync } from '../../hooks/useAuthSync';
import sidebarLogo from '../../simple_logo.png';
import './Sidebar.css';

const Sidebar = ({ isOpen, toggleSidebar, closeSidebar, signOutRedirect, isMobile, tempExpanded, onTempExpand, onTempCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getAppRole, hasPermission, isMedicalDataEnabled } = useAuthSync();
  const appRole = getAppRole();
  const isAdminOrOwner = appRole === 'admin' || appRole === 'owner';

  const navRef = useRef(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  const [expandedMenus, setExpandedMenus] = useState({
    patients: false,
    appointments: location.pathname.startsWith('/appointments'),
    laboratoryOrders: location.pathname.startsWith('/laboratory-orders'),
    reports: false,
    settings: location.pathname.startsWith('/settings'),
    integrations: location.pathname.startsWith('/integrations')
  });

  // Colapsar todos los menus cuando el sidebar se cierra en desktop y no está temporalmente expandido
  useEffect(() => {
    if (!isMobile && !isOpen && !tempExpanded) {
      setExpandedMenus({
        patients: false,
        appointments: false,
        laboratoryOrders: false,
        reports: false,
        settings: false,
        integrations: false
      });
    }
  }, [isOpen, isMobile, tempExpanded]);

  // Check if the nav has hidden content below the visible area
  // SCROLL_THRESHOLD accounts for sub-pixel rounding in scroll position calculations
  const SCROLL_THRESHOLD = 1;
  const checkScrollIndicator = useCallback(() => {
    const nav = navRef.current;
    if (!nav) return;
    const hasMoreBelow = nav.scrollHeight > nav.clientHeight + nav.scrollTop + SCROLL_THRESHOLD;
    setShowScrollIndicator(hasMoreBelow);
  }, []);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    checkScrollIndicator();
    nav.addEventListener('scroll', checkScrollIndicator);
    const resizeObserver = new ResizeObserver(checkScrollIndicator);
    resizeObserver.observe(nav);
    return () => {
      nav.removeEventListener('scroll', checkScrollIndicator);
      resizeObserver.disconnect();
    };
  }, [checkScrollIndicator]);

  // Recalculate when menus expand or collapse
  useEffect(() => {
    checkScrollIndicator();
  }, [expandedMenus, checkScrollIndicator]);

  const toggleMenu = (menuKey) => {
    // Si el sidebar está cerrado en desktop, activar expansión temporal
    if (!isMobile && !isOpen && !tempExpanded) {
      onTempExpand();
      return;
    }

    // Si el sidebar está cerrado pero temporalmente expandido, expandir el submenu
    if (!isMobile && !isOpen && tempExpanded) {
      setExpandedMenus(prev => ({
        ...prev,
        [menuKey]: !prev[menuKey]
      }));
      return;
    }

    // Comportamiento normal cuando el sidebar está abierto
    if (isOpen || isMobile) {
      setExpandedMenus(prev => ({
        ...prev,
        [menuKey]: !prev[menuKey]
      }));
    }
  };

  const handleOverlayClick = (e) => {
    // Solo cerrar si se hace click en el overlay, no en el sidebar
    if (e.target === e.currentTarget) {
      closeSidebar();
    }
  };

  const handleLogout = () => {
    // Cerrar el sidebar en móvil o colapsar expansión temporal antes de hacer logout
    if (isMobile) {
      closeSidebar();
    } else if (tempExpanded) {
      onTempCollapse();
    }
    // Ejecutar la función de logout
    signOutRedirect();
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      closeSidebar();
    } else if (tempExpanded) {
      onTempCollapse();
    }
  };

  const handleMenuItemClick = (item) => {
    // If sidebar is closed and not temporarily expanded, trigger temp expansion
    if (!isMobile && !isOpen && !tempExpanded) {
      onTempExpand();
      // If the item has a submenu, automatically expand it when temp expanding
      if (item.hasSubmenu) {
        setExpandedMenus(prev => ({
          ...prev,
          [item.id]: true
        }));
      }
      return;
    }

    // Normal behavior - navigate if no submenu, or toggle submenu
    if (item.hasSubmenu) {
      toggleMenu(item.id);
    } else {
      handleNavigation(item.link);
    }
  };

  const handleLogoutClick = () => {
    // If sidebar is closed and not temporarily expanded, trigger temp expansion
    if (!isMobile && !isOpen && !tempExpanded) {
      onTempExpand();
      return;
    }

    // Normal logout behavior
    handleLogout();
  };

  const menuItems = [
    {
      id: 'dashboard',
      icon: <FaHome />,
      text: 'Dashboard',
      link: '/',
      hasSubmenu: false
    },
    {
      id: 'patients',
      icon: <FaUsers />,
      text: 'Pacientes',
      link: '/patients',
      hasSubmenu: true,
      submenu: [
        { id: 'all-patients', text: 'Todos los Pacientes', link: '/patients/list' },
        { id: 'new-patient', text: 'Nuevo Paciente', link: '/patients/new' },
        // { id: 'patient-history', text: 'Historial', link: '/patients/history' }
      ]
    },
    {
      id: 'appointments',
      icon: <FaCalendarAlt />,
      text: 'Citas',
      link: '/appointments',
      hasSubmenu: true,
      submenu: [
        { id: 'all-appointments', text: 'Todas las Citas', link: '/appointments' },
        { id: 'new-appointment', text: 'Nueva Cita', link: '/appointments/new' },
        { id: 'calendar-blocks', text: 'Bloqueos', link: '/appointments/blocks' },
        // { id: 'calendar', text: 'Calendario', link: '/appointments/calendar' }
      ]
    },
    // {
    //   id: 'billing',
    //   icon: <FaFileInvoiceDollar />,
    //   text: 'Facturación',
    //   link: '/billing',
    //   hasSubmenu: false
    // },
    {
      id: 'reports',
      icon: <FaChartBar />,
      text: 'Reportes',
      link: '/reports',
      hasSubmenu: true,
      submenu: [
        ...(hasPermission('reports:viewfinancial') ? [
          { id: 'financial-reports', text: 'Reportes Financieros', link: '/reports/financial' },
        ] : []),
        ...(hasPermission('reports:viewappointments') ? [
          { id: 'appointment-reports', text: 'Reportes de Citas', link: '/reports/appointments' },
        ] : []),
      ]
    },
    {
      id: 'supplies',
      icon: <FaBoxes />,
      text: 'Insumos',
      link: '/supplies',
      hasSubmenu: false
    },
    {
      id: 'laboratoryOrders',
      icon: <FaFlask />,
      text: 'Laboratorio Dental',
      link: '/laboratory-orders',
      hasSubmenu: true,
      submenu: [
        { id: 'all-laboratory-orders', text: 'Todas las Ordenes', link: '/laboratory-orders' },
        { id: 'new-laboratory-order', text: 'Nueva Orden', link: '/laboratory-orders/new' },
        { id: 'providers', text: 'Proveedores', link: '/laboratory-orders/providers' }
      ]
    },
    {
      id: 'settings',
      icon: <FaCog />,
      text: 'Configuración',
      link: '/settings',
      hasSubmenu: true,
      submenu: [
        { id: 'profile', text: 'Mi perfil', link: '/settings/profile' },
        ...(isAdminOrOwner ? [
          { id: 'subscription', text: 'Suscripción', link: '/settings/subscription' },
        ] : []),
        { id: 'clinic', text: 'Clínica', link: '/settings/clinic' },
        { id: 'users', text: 'Usuarios', link: '/settings/users' },
        ...(isAdminOrOwner ? [
          { id: 'roles', text: 'Roles', link: '/settings/roles' }
        ] : []),
        ...(isAdminOrOwner ? [
          { id: 'locations', text: 'Ubicaciones', link: '/settings/locations' }
        ] : []),
        ...(isMedicalDataEnabled() ? [
          { id: 'farmacos', text: 'Fármacos', link: '/settings/farmacos' }
        ] : []),
        { id: 'procedures', text: 'Procedimientos', link: '/settings/procedures' },
        ...(isMedicalDataEnabled() ? [
          { id: 'diagnoses', text: 'Diagnósticos', link: '/settings/diagnoses' }
        ] : []),
        ...(isAdminOrOwner ? [
          { id: 'paymentForms', text: 'Formas de Pago', link: '/settings/payment-forms' }
        ] : []),
        // { id: 'insurance', text: 'Aseguradoras', link: '/settings/insurance' },
        { id: 'preferences', text: 'Preferencias', link: '/settings/preferences' },
        { id: 'billing', text: 'Facturación', link: '/settings/billing' },
      ]
    },
    // {
    //   id: 'integrations',
    //   icon: <FaPlug />,
    //   text: 'Integraciones',
    //   link: '/integrations',
    //   hasSubmenu: true,
    //   submenu: [
    //     { id: 'google-calendar', text: 'Google Calendar', link: '/integrations' },
    //   ]
    // }
  ];

  return (
    <>
      {/* Overlay para móvil */}
      {isMobile && isOpen && (
        <div className="sidebar-overlay" onClick={handleOverlayClick}></div>
      )}

      {/* Overlay for temporary expansion */}
      {tempExpanded && !isMobile && (
        <div className="sidebar-temp-overlay" onClick={onTempCollapse}></div>
      )}

      <div className={`sidebar ${isOpen || tempExpanded ? 'open' : 'closed'} ${isMobile ? 'mobile' : 'desktop'} ${tempExpanded ? 'temp-expanded' : ''}`}>
        {/* Logo Section */}
        <div className="sidebar-logo">
          {/* Botón hamburguesa para desktop (Gmail-like) */}
          {!isMobile && (
            <button className="sidebar-hamburger-btn" onClick={toggleSidebar} title={isOpen ? 'Contraer sidebar' : 'Expandir sidebar'}>
              <FaBars />
            </button>
          )}

          <div className="sidebar-logo-container">
            <div className="sidebar-logo-fallback">
              <img src={sidebarLogo} alt="Dentaris" className="sidebar-logo-image" />
              <span>Dentaris</span>
            </div>
          </div>

          {/* Botón de cerrar para móvil */}
          {isMobile && (
            <button className="sidebar-close-btn" onClick={closeSidebar}>
              <FaTimes />
            </button>
          )}
        </div>

        {/* Menu Items */}
        <div className="sidebar-nav-wrapper">
          <nav className="sidebar-nav" ref={navRef}>
            {menuItems.filter(item => !item.hasSubmenu || item.submenu.length > 0).map((item) => {
              // For items with submenus, only highlight if there's an exact match and no submenu item matches
              const isParentActive = item.hasSubmenu
                ? location.pathname === item.link && !item.submenu.some(sub => location.pathname === sub.link)
                : location.pathname === item.link;

              return (
                <div key={item.id} className="menu-item-container">
                  <div
                    className={`menu-item ${item.hasSubmenu ? 'has-submenu' : ''} ${isParentActive ? 'active' : ''
                      }`}
                    onClick={() => handleMenuItemClick(item)}
                    data-tooltip={item.text}
                  >
                    <div className="menu-item-content">
                      <span className="menu-icon">{item.icon}</span>
                      <span className="menu-text">{item.text}</span>
                    </div>
                    {item.hasSubmenu && (
                      <span className="menu-arrow">
                        {expandedMenus[item.id] ? <FaChevronDown /> : <FaChevronRight />}
                      </span>
                    )}
                  </div>

                  {item.hasSubmenu && expandedMenus[item.id] && (
                    <div className="submenu">
                      {item.submenu.map((subItem) => (
                        <div
                          key={subItem.id}
                          className={`submenu-item ${location.pathname === subItem.link ? 'active' : ''
                            }`}
                          onClick={() => handleNavigation(subItem.link)}
                        >
                          <span className="submenu-text">{subItem.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
          {showScrollIndicator && (
            <div className="sidebar-scroll-indicator" aria-hidden="true">
              <FaChevronDown className="sidebar-scroll-indicator-icon" />
            </div>
          )}
        </div>

        {/* Logout Section */}
        <div className="sidebar-footer">
          <div className="footer-item updates-link" onClick={() => handleNavigation('/ayuda')} data-tooltip="Ayuda">
            <span className="menu-icon"><FaQuestionCircle /></span>
            <span className="menu-text">Ayuda</span>
          </div>
          <div className="footer-item updates-link" onClick={() => handleNavigation('/updates')} data-tooltip="Actualizaciones">
            <span className="menu-icon"><FaBell /></span>
            <span className="menu-text">Actualizaciones</span>
          </div>
          <div className="footer-item logout-link" onClick={handleLogoutClick} data-tooltip="Cerrar Sesión">
            <span className="menu-icon"><FaSignOutAlt /></span>
            <span className="menu-text">Cerrar Sesión</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar; 